import React, { useEffect, useState } from "react";
import Modal from "src/components/ui/Modal";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { toast } from "react-toastify";

const MODULES = [
  "dashboard",
  "products",
  "inventory",
  "suppliers",
  "sales",
  "purchases",
  "adjustments",
  "reports",
  "users",
];

const ACTIONS = ["view", "create", "edit", "delete"];

interface AddEditRoleModalProps {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  existingRole?: any;
  isAdminAuth?: boolean;
}

const AddEditRoleModal: React.FC<AddEditRoleModalProps> = ({
  open,
  onClose,
  tenantId,
  existingRole,
  isAdminAuth = false,
}) => {
  const { createTenantRole, editTenantRole } = useAppSelector(
    (state) => state.tenant
  );
  const dispatch = useAppDispatch();

  const [role, setRole] = useState({
    name: existingRole?.name || "",
    description: existingRole?.description || "",
    permissions: existingRole?.permissions || {},
  });
  const [errors, setErrors] = useState({
    name: "",
    description: "",
    permissions: "",
  });

  useEffect(() => {
    if (createTenantRole) {
      if (createTenantRole?.data) {
        toast.success("Tenant Role created successfully");
        onClose();
      } else if (createTenantRole?.error) {
        toast.error("Error while creating tenant role details");
      }
    }
  }, [createTenantRole]);

  useEffect(() => {
    if (editTenantRole) {
      if (editTenantRole?.data) {
        toast.success("Tenant Role updated successfully");
        onClose();
      } else if (editTenantRole?.error) {
        toast.error("Error while updating tenant role details");
      }
    }
  }, [editTenantRole]);

  const togglePermission = (module: string, action: string) => {
    const updated = { ...role.permissions };
    updated[module] = updated[module] || [];

    if (updated[module].includes(action)) {
      updated[module] = updated[module].filter((a: string) => a !== action);
    } else {
      updated[module].push(action);
    }

    setRole({ ...role, permissions: updated });
  };

  const handleSave = () => {
    if (!role.name) setErrors({ ...errors, name: "Required!" });
    else if (!role.description)
      setErrors({ ...errors, description: "Required!" });
    else if (Object.keys(role.permissions).length === 0)
      toast.error("Select atleast one permission");

    if (
      !role.name ||
      !role.description ||
      Object.keys(role.permissions).length === 0
    )
      return;

    const payload = tenantId ? { ...role, tenantId } : role;

    if (existingRole) {
      dispatch(
        tenantActions.editTenantRoleRequest({
          id: existingRole._id,
          data: payload,
          isAdminAuth,
        })
      );
    } else {
      dispatch(tenantActions.createTenantRoleRequest({ ...payload, isAdminAuth }));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={existingRole ? "Edit Role" : "Add New Role"}
      //   width="max-w-4xl"
      size="3xl"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Role Name"
            placeholder="e.g. Inventory Manager"
            value={role.name}
            onChange={(e) => {
              const { value } = e.target;
              setRole({ ...role, name: value });
              setErrors({ ...errors, name: !value ? "Required!" : "" });
            }}
            error={!!errors.name}
            helperText={errors.name}
          />
          <Input
            label="Description"
            placeholder="Optional description"
            value={role.description}
            onChange={(e) => {
              const { value } = e.target;
              setRole({ ...role, description: value });
              setErrors({ ...errors, description: !value ? "Required!" : "" });
            }}
            error={!!errors.description}
            helperText={errors.description}
          />
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-2 text-left">Module</th>
                {ACTIONS.map((a) => (
                  <th key={a} className="capitalize p-2 text-center">
                    {a}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MODULES.map((mod) => (
                <tr key={mod} className="border-t">
                  <td className="p-2 capitalize font-medium">{mod}</td>
                  {ACTIONS.map((action) => (
                    <td key={action} className="p-2 text-center">
                      <input
                        type="checkbox"
                        className="cursor-pointer"
                        checked={role.permissions[mod]?.includes(action)}
                        onChange={() => togglePermission(mod, action)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            isLoading={createTenantRole.loading || editTenantRole.loading}
            disabled={createTenantRole.loading || editTenantRole.loading}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={createTenantRole.loading || editTenantRole.loading}
            disabled={createTenantRole.loading || editTenantRole.loading}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEditRoleModal;
